export const COMPARADOR_FUNDOS_SOURCE_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  PARTIAL: 'partial',
  ERROR: 'error'
};

export const COMPARADOR_FUNDOS_SOURCES = [
  'detalhe',
  'cotacao',
  'indicador',
  'patrimonio',
  'correlacao'
];

const wCoreSources = ['detalhe', 'cotacao'];

function pvReadResponseData(pResponse) {
  return pResponse?.data ?? pResponse ?? null;
}

function pvStringOrNull(pValue) {
  if (pValue === null || typeof pValue === 'undefined' || pValue === '') {
    return null;
  }

  return String(pValue);
}

function pvNormalizeCorrelacaoAtivoId(pValue) {
  const xValue = pvStringOrNull(pValue);

  return xValue ? xValue.replace(/^ATIVO_/, '') : null;
}

function pvNormalizeSeries(pResponse, pValueKeys = []) {
  const xData = pvReadResponseData(pResponse);

  if (!Array.isArray(xData)) {
    return [];
  }

  return xData.reduce((pResult, pItem) => {
    if (!pItem?.data) {
      return pResult;
    }

    const xValueKey = pValueKeys.find(pKey => pItem[pKey] !== null && pItem[pKey] !== undefined);

    if (!xValueKey) {
      return pResult;
    }

    return [
      ...pResult,
      {
        data: pItem.data,
        [pValueKeys[0]]: pItem[xValueKey]
      }
    ];
  }, []);
}

/** Normaliza o detalhe cadastral sem preencher campos ausentes. */
export function normalizeComparadorFundosDetalhe(pResponse) {
  const xRawData = pvReadResponseData(pResponse);
  const xData = Array.isArray(xRawData) ? xRawData[0] : xRawData;

  if (!xData || typeof xData !== 'object') {
    return null;
  }

  const xNormalizedData = {
    ...xData,
    ifeed_ativo_id: pvStringOrNull(xData.ifeed_ativo_id),
    ativo_id: pvStringOrNull(xData.ativo_id),
    ticker: xData.ativo_seuid || xData.ticker || null,
    ativo: xData.ativo || xData.nome || null,
    mercado: xData.mercado || xData.mercado_id || null,
    pessoa_id: pvStringOrNull(xData.emissor_pessoa_id || xData.pessoa_id),
    indicador_id: pvStringOrNull(
      xData.indicador_id ||
        xData.benchmark_indicador_id ||
        xData.fundo?.benchmark_indicador_id ||
        xData.benchmark_id
    )
  };

  return xNormalizedData;
}

/** Normaliza o histórico do fundo mantendo apenas pontos observados. */
export function normalizeComparadorFundosCotacao(pResponse) {
  const xData = pvReadResponseData(pResponse);

  if (!Array.isArray(xData)) {
    return [];
  }

  return xData
    .filter(
      pItem => pItem?.data && (pItem.cotacao !== undefined || pItem.cotacao_ajustada !== undefined)
    )
    .map(pItem => ({
      data: pItem.data,
      ...(pItem.cotacao !== undefined && { cotacao: pItem.cotacao }),
      ...(pItem.cotacao_ajustada !== undefined && {
        cotacao_ajustada: pItem.cotacao_ajustada
      }),
      ...(pItem.cotacao_ajustada_ex_receitas !== undefined && {
        cotacao_ajustada_ex_receitas: pItem.cotacao_ajustada_ex_receitas
      }),
      ...(pItem.variacao_diaria !== undefined && {
        variacao_diaria: pItem.variacao_diaria
      })
    }));
}

/** Normaliza a série de indicador ou inflação. */
export function normalizeComparadorFundosIndicador(pResponse) {
  return pvNormalizeSeries(pResponse, ['cotacao', 'valor']);
}

/** Normaliza a série de patrimônio líquido. */
export function normalizeComparadorFundosPatrimonio(pResponse) {
  return pvNormalizeSeries(pResponse, ['patrimonio_liquido', 'pl', 'valor']);
}

/** Normaliza a matriz retornada pelo serviço de correlação. */
export function normalizeComparadorFundosCorrelacao(pResponse) {
  const xData = pvReadResponseData(pResponse);
  const xCorrelacoes = Array.isArray(xData) ? xData : xData?.correlacoes;

  if (!Array.isArray(xCorrelacoes)) {
    return [];
  }

  return xCorrelacoes
    .filter(pItem => pItem && typeof pItem.correlacao === 'number')
    .map(pItem => {
      const [xAtivo1Id, xAtivo2Id] = Array.isArray(pItem.ids) ? pItem.ids : [];

      return {
        ativo_1_id: pvNormalizeCorrelacaoAtivoId(pItem.ativo_1_id || pItem.ativo_id || xAtivo1Id),
        ativo_2_id: pvNormalizeCorrelacaoAtivoId(
          pItem.ativo_2_id || pItem.comparado_ativo_id || xAtivo2Id
        ),
        correlacao: pItem.correlacao
      };
    });
}

function pvCreateSourceState() {
  return {
    status: COMPARADOR_FUNDOS_SOURCE_STATUS.IDLE,
    data: null,
    error: null,
    requestId: null
  };
}

function pvCreateFundoState() {
  return {
    status: COMPARADOR_FUNDOS_SOURCE_STATUS.IDLE,
    sources: COMPARADOR_FUNDOS_SOURCES.reduce((pResult, pSource) => {
      return { ...pResult, [pSource]: pvCreateSourceState() };
    }, {})
  };
}

export function createComparadorFundosDataState() {
  return { fundos: {} };
}

function pvGetFundoStatus(pSources) {
  const xCoreStates = wCoreSources.map(pSource => pSources[pSource]);
  const xAllStates = Object.values(pSources);
  const xHasReady = xAllStates.some(
    pSource => pSource.status === COMPARADOR_FUNDOS_SOURCE_STATUS.READY
  );
  const xHasLoading = xAllStates.some(
    pSource => pSource.status === COMPARADOR_FUNDOS_SOURCE_STATUS.LOADING
  );
  const xHasPartial = xAllStates.some(
    pSource => pSource.status === COMPARADOR_FUNDOS_SOURCE_STATUS.PARTIAL
  );
  const xHasError = xAllStates.some(
    pSource => pSource.status === COMPARADOR_FUNDOS_SOURCE_STATUS.ERROR
  );
  const xCoreReady = xCoreStates.every(
    pSource => pSource.status === COMPARADOR_FUNDOS_SOURCE_STATUS.READY
  );

  if (xCoreReady && !xHasError) {
    return COMPARADOR_FUNDOS_SOURCE_STATUS.READY;
  }

  if (xHasReady || xHasPartial) {
    return COMPARADOR_FUNDOS_SOURCE_STATUS.PARTIAL;
  }

  if (xHasLoading) {
    return COMPARADOR_FUNDOS_SOURCE_STATUS.LOADING;
  }

  if (xHasError) {
    return COMPARADOR_FUNDOS_SOURCE_STATUS.ERROR;
  }

  return COMPARADOR_FUNDOS_SOURCE_STATUS.IDLE;
}

/** Reducer puro de fontes, com proteção contra respostas obsoletas. */
export function comparadorFundosDataReducer(pState, pAction) {
  if (!pAction?.fundoId || !pAction?.source) {
    return pState;
  }

  const xFundoId = String(pAction.fundoId);
  const xFundo = pState.fundos[xFundoId] || pvCreateFundoState();
  const xCurrentSource = xFundo.sources[pAction.source] || pvCreateSourceState();

  if (pAction.type !== 'SOURCE_LOADING' && xCurrentSource.requestId !== pAction.requestId) {
    return pState;
  }

  const xNextSource = {
    SOURCE_LOADING: {
      status: COMPARADOR_FUNDOS_SOURCE_STATUS.LOADING,
      data: xCurrentSource.data,
      error: null,
      requestId: pAction.requestId
    },
    SOURCE_READY: {
      status: COMPARADOR_FUNDOS_SOURCE_STATUS.READY,
      data: pAction.data,
      error: null,
      requestId: pAction.requestId
    },
    SOURCE_PARTIAL: {
      status: COMPARADOR_FUNDOS_SOURCE_STATUS.PARTIAL,
      data: pAction.data,
      error: pAction.error || null,
      requestId: pAction.requestId
    },
    SOURCE_ERROR: {
      status: COMPARADOR_FUNDOS_SOURCE_STATUS.ERROR,
      data: xCurrentSource.data,
      error: pAction.error,
      requestId: pAction.requestId
    }
  }[pAction.type];

  if (!xNextSource) {
    return pState;
  }

  const xSources = {
    ...xFundo.sources,
    [pAction.source]: xNextSource
  };

  return {
    ...pState,
    fundos: {
      ...pState.fundos,
      [xFundoId]: {
        status: pvGetFundoStatus(xSources),
        sources: xSources
      }
    }
  };
}
